// Populated at container start (deploy/docker-entrypoint.sh's envsubst step,
// preview environments only) via deploy/env.js.template -- lets one built
// image point at whichever backend preview it's attached to without a
// rebuild, since VITE_* vars are otherwise baked in at build time. Absent
// (undefined) for every other build (local, staging, production), which all
// fall back to VITE_DJANGO_URL below exactly as before.
declare global {
    interface Window {
        __RUNTIME_CONFIG__?: { djangoURL?: string };
    }
}

interface Config {
    debug: boolean;
    taxCalcSubmissionEventId?: string;
    frontendURL: string;
    companyName?: string;
    companyURL?: string;
    djangoURL?: string;
    /** The websocket origin, served by a different process from `djangoURL`. Unset means no sockets. */
    wsURL?: string;
    environment?: string;
    datadog: {
        rumAppId?: string;
        rumClientToken?: string;
    };
    docusign: {
        integrationKey?: string;
    };
    impact: {
        advocateLoaderUrl?: string;
        tenantAlias?: string;
    };
    googleAnalytics: {
        enabled: boolean;
    };
    sourceVersion?: string;
    otp: {
        timeResendLimitSeconds: number;
        allowedVerifyAttempts: number;
        allowedResendAttempts: number;
        validitySeconds: number;
    };
    companyEmail?: string;
    helloSign: {
        clientId?: string;
    };
    facebook: {
        token?: string;
    };
    stripe: {
        apiKey?: string;
    };
    retool: {
        url?: string;
    };
    statsig: {
        sdkKey?: string;
    };
    hurdlr: {
        clientId?: string;
        url: {
            web?: string;
            api?: string;
        };
    };
    sfmc: {
        trainingCall: {
            journeyKey: string;
            exitJourneyKeyList: string[];
        };
    };
    maintenance: {
        scheduleDate?: string;
    };
    socialIntents: {
        appKey?: string;
    };
    amplitude: {
        orgSlug: string;
        projectId?: string;
    };
}

const config: Config = {
    debug: import.meta.env.VITE_DEBUG === 'true',
    taxCalcSubmissionEventId: import.meta.env.VITE_TAX_CALC_SUBMISSION_EVENT_ID,
    frontendURL: import.meta.env.VITE_FE_URL,
    companyName: import.meta.env.VITE_COMPANY_NAME,
    companyURL: import.meta.env.VITE_COMPANY_URL,
    djangoURL:
        // eslint-disable-next-line no-underscore-dangle
        (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__?.djangoURL) || import.meta.env.VITE_DJANGO_URL,
    wsURL: import.meta.env.VITE_WS_URL,
    environment: import.meta.env.VITE_ENVIRONMENT,
    datadog: {
        rumAppId: import.meta.env.VITE_DATADOG_RUM_APP_ID,
        rumClientToken: import.meta.env.VITE_DATADOG_RUM_CLIENT_TOKEN,
    },
    docusign: {
        integrationKey: import.meta.env.VITE_DOCUSIGN_INTEGRATION_KEY,
    },
    impact: {
        advocateLoaderUrl: import.meta.env.VITE_IMPACT_ADVOCATE_LOADER_URL,
        tenantAlias: import.meta.env.VITE_IMPACT_TENANT_ALIAS,
    },
    googleAnalytics: {
        enabled: import.meta.env.VITE_GOOGLE_ANALYTICS_ENABLED === 'true',
    },
    sourceVersion: import.meta.env.VITE_SOURCE_VERSION,
    otp: {
        allowedResendAttempts: Number(import.meta.env.VITE_OTP_ALLOWED_RESEND_ATTEMPTS) || 3,
        allowedVerifyAttempts: Number(import.meta.env.VITE_OTP_ALLOWED_VERIFY_ATTEMPTS) || 5,
        timeResendLimitSeconds: Number(import.meta.env.VITE_OTP_TIME_RESEND_LIMIT_SECONDS) || 0,
        validitySeconds: Number(import.meta.env.VITE_OTP_VALIDITY_SECONDS) || 1800, // 30 minutes
    },
    companyEmail: import.meta.env.VITE_COMPANY_EMAIL,
    helloSign: {
        clientId: import.meta.env.VITE_HS_CLIENT_ID,
    },
    facebook: {
        token: import.meta.env.VITE_FACEBOOK_TOKEN,
    },
    stripe: {
        apiKey: import.meta.env.VITE_STRIPE,
    },
    retool: {
        url: import.meta.env.VITE_RETOOL_URL,
    },
    statsig: {
        sdkKey: import.meta.env.VITE_STATSIG_SDK_KEY,
    },
    hurdlr: {
        clientId: import.meta.env.VITE_HURDLR_CLIENT_ID,
        url: {
            api: import.meta.env.VITE_HURDLR_API_URL,
            web: import.meta.env.VITE_HURDLR_WEB_URL,
        },
    },
    sfmc: {
        trainingCall: {
            journeyKey: import.meta.env.VITE_TRAINING_CALL_JOURNEY_KEY ?? '',
            exitJourneyKeyList: (import.meta.env.VITE_TRAINING_CALL_EXIT_JOURNEY_KEYS ?? '').split(','),
        },
    },
    maintenance: {
        scheduleDate: import.meta.env.VITE_MAINTENANCE_SCHEDULE_DATE || '',
    },
    socialIntents: {
        appKey: import.meta.env.VITE_SOCIAL_INTENTS_APP_KEY,
    },
    amplitude: {
        orgSlug: import.meta.env.VITE_AMPLITUDE_ORG_SLUG ?? 'collective1',
        projectId: import.meta.env.VITE_AMPLITUDE_PROJECT_ID,
    },
};

/** True only in the real production environment. Prefer this over inlining `config.environment === 'production'`. */
export const isProduction = (): boolean => config.environment === 'production';

export default config;
